import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";

import { hostelService, type Hostel, type HostelFee, type HostelPayment } from "../../services/hostelService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { byNumber, byText, humanise, reversed, statusDot, type RegisterConfig } from "../register/registerLogic";
import { FEE_TYPES, day, labelFrom, money } from "./hostelLogic";

const feeInForce = (fee: HostelFee, today: string) => fee.is_active && fee.effective_from <= today && (!fee.effective_until || fee.effective_until >= today);
const feeConfig = (today: string): RegisterConfig<HostelFee> => ({
  search: (row) => [row.name, row.hostel_name, row.description].join(" "),
  views: [
    { key: "current", label: "In force", match: (row) => feeInForce(row, today) },
    { key: "ended", label: "Ended or stopped", match: (row) => !feeInForce(row, today) },
    { key: "all", label: "All" },
  ],
  category: { label: "Residence", value: (row) => row.hostel_name },
  sorts: [
    { key: "residence", label: "Residence", compare: byText<HostelFee>((row) => `${row.hostel_name} ${row.fee_type}`) },
    { key: "-amount", label: "Largest first", compare: reversed(byNumber<HostelFee>((row) => row.amount)) },
  ],
});
const chargeConfig: RegisterConfig<HostelPayment> = {
  search: (row) => [row.student_name, row.student_id, row.fee_name, row.invoice_number, row.receipt_number].join(" "),
  views: [
    { key: "owing", label: "Owing", match: (row) => row.status === "pending" || row.status === "overdue" },
    { key: "paid", label: "Paid", match: (row) => row.status === "paid" },
    { key: "all", label: "All" },
  ],
  date: { label: "Due", value: (row) => row.due_date },
  sorts: [
    { key: "-due", label: "Newest first", compare: reversed(byText<HostelPayment>((row) => row.due_date)) },
    { key: "due", label: "Oldest first", compare: byText<HostelPayment>((row) => row.due_date) },
  ],
};

function FeeForm({ fee, hostels, onClose, onDone }: { fee?: HostelFee; hostels: Hostel[]; onClose: () => void; onDone: (row: HostelFee) => void }) {
  const [recurring, setRecurring] = useState(fee?.is_recurring ?? true);
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (fee ? hostelService.updateFee({ id: fee.id, data }) : hostelService.createFee(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim() || null;
    save.mutate({
      hostel: Number(text("hostel")),
      fee_type: text("fee_type"),
      name: text("name"),
      amount: text("amount"),
      effective_from: text("effective_from"),
      effective_until: text("effective_until"),
      is_recurring: recurring,
      recurring_period: recurring ? text("period") : null,
      is_mandatory: form.get("mandatory") === "on",
      description: text("description"),
      is_active: true,
    });
  };
  return (
    <FormModal
      id="hostel-fee"
      title={fee ? `Edit ${fee.name}` : "Add a boarding fee"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The fee could not be saved.") : undefined}
      saveLabel={fee ? "Save changes" : "Add the fee"}
      note="The bursar raises charges from this list."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The fee" divided={false}>
        <Field label="Residence">
          <select required name="hostel" defaultValue={fee?.hostel ?? (hostels.length === 1 ? hostels[0].id : "")} className="input mt-1">
            <option value="">Choose a residence</option>
            {hostels.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Kind">
          <select name="fee_type" defaultValue={fee?.fee_type ?? "monthly"} className="input mt-1">
            {FEE_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Name" hint="As families will see it, e.g. Boarding fee, Term 3 2026.">
          <input required name="name" maxLength={100} defaultValue={fee?.name} className="input mt-1" />
        </Field>
        <Field label="Amount (UGX)">
          <input required type="number" name="amount" min={1} step="any" defaultValue={fee?.amount} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="From">
          <input required type="date" name="effective_from" defaultValue={fee?.effective_from ?? todayIso()} className="input mt-1" />
        </Field>
        <Field label="Until" optional>
          <input type="date" name="effective_until" defaultValue={fee?.effective_until ?? ""} className="input mt-1" />
        </Field>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={recurring} onChange={(event) => setRecurring(event.target.checked)} className="h-4 w-4 rounded border-gray-300" /> Charged again every period
        </label>
        {recurring ? (
          <Field label="Every">
            <select name="period" defaultValue={fee?.recurring_period ?? "monthly"} className="input mt-1">
              <option value="monthly">Month</option>
              <option value="quarterly">Quarter</option>
              <option value="semester">Semester</option>
              <option value="yearly">Year</option>
            </select>
          </Field>
        ) : <span />}
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" name="mandatory" defaultChecked={fee?.is_mandatory ?? true} className="h-4 w-4 rounded border-gray-300" /> Every boarder pays it
        </label>
        <Field label="Description" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={fee?.description ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** What boarding costs in each residence; families also see their own charges. */
export function FeesPanel({ fees, charges, hostels, isLoading, isError, onRetry, canManage, isFamily, onChanged }: {
  fees?: HostelFee[];
  charges?: HostelPayment[];
  hostels: Hostel[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  isFamily: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<HostelFee | "new" | null>(null);
  const stop = useMutation({
    mutationFn: (row: HostelFee) => hostelService.updateFee({ id: row.id, data: { is_active: !row.is_active } }),
    onSuccess: (row) => {
      setNotice({ tone: "success", text: row.is_active ? `${row.name} is in use again.` : `${row.name} is stopped; no new charges can be raised from it.` });
      onChanged();
    },
  });

  const feeColumns: RegisterColumn<HostelFee>[] = [
    { header: "Fee", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem]" },
    { header: "Residence", cell: (row) => row.hostel_name, exportValue: (row) => row.hostel_name, className: "text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(FEE_TYPES, row.fee_type), exportValue: (row) => labelFrom(FEE_TYPES, row.fee_type), className: "text-gray-700" },
    { header: "Amount", cell: (row) => money(row.amount), exportValue: (row) => Number(row.amount), align: "right" },
    { header: "Every", cell: (row) => (row.is_recurring ? humanise(row.recurring_period) : "Once"), exportValue: (row) => (row.is_recurring ? humanise(row.recurring_period) : "Once"), className: "text-gray-700" },
    { header: "From", cell: (row) => day(row.effective_from), exportValue: (row) => day(row.effective_from), className: "tabular-nums text-gray-700" },
    { header: "Until", cell: (row) => day(row.effective_until) || "—", exportValue: (row) => day(row.effective_until), className: "tabular-nums text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot label={feeInForce(row, today) ? "In force" : row.is_active ? "Not in force" : "Stopped"} dot={feeInForce(row, today) ? "bg-emerald-500" : "bg-gray-400"} />, exportValue: (row) => (feeInForce(row, today) ? "In force" : "Not in force") },
  ];
  const chargeColumns: RegisterColumn<HostelPayment>[] = [
    { header: "Due", cell: (row) => day(row.due_date), exportValue: (row) => day(row.due_date), className: "tabular-nums text-gray-700" },
    { header: "Boarder", cell: (row) => row.student_name, exportValue: (row) => row.student_name, className: "text-gray-900" },
    { header: "Fee", cell: (row) => row.fee_name, exportValue: (row) => row.fee_name, className: "text-gray-700" },
    { header: "Amount", cell: (row) => money(row.amount), exportValue: (row) => Number(row.amount), align: "right" },
    { header: "Invoice", cell: (row) => row.invoice_number || "—", exportValue: (row) => row.invoice_number ?? "", className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot label={humanise(row.status)} dot={statusDot(row.status)} />, exportValue: (row) => humanise(row.status) },
  ];

  const fee = viewing !== null ? fees?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Boarding fees"
        description={canManage ? "The fee list the bursar raises charges from. Open a fee to edit or stop it." : "What boarding costs."}
        actions={canManage && (
          <button type="button" disabled={!hostels.length} onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add a fee
          </button>
        )}
        rows={fees}
        config={feeConfig(today)}
        columns={feeColumns}
        rowKey={(row) => row.id}
        onOpen={canManage ? (row) => {
          stop.reset();
          setViewing(row.id);
        } : undefined}
        noun={["fee", "fees"]}
        exportName={{ file: "boarding-fees", title: "Boarding Fees" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No boarding fees set yet." : "No boarding fees published."}
      />

      {charges && (isFamily || canManage) && (
        <ClientRegister
          title={isFamily ? "Your boarding charges" : "Boarding charges"}
          description={isFamily ? "Charges raised by the bursar. Pay at the bursar's office or by the usual school channels." : "Raised and received by the bursar, shown here for reference."}
          rows={charges}
          config={chargeConfig}
          columns={chargeColumns}
          rowKey={(row) => row.id}
          noun={["charge", "charges"]}
          exportName={{ file: "boarding-charges", title: "Boarding Charges" }}
          emptyText="No boarding charges yet."
          canExport={!isFamily}
        />
      )}

      {fee && !editing && (
        <DetailsModal
          title={fee.name}
          headline={{ label: labelFrom(FEE_TYPES, fee.fee_type), value: money(fee.amount), note: `${fee.hostel_name} · ${fee.is_recurring ? `every ${humanise(fee.recurring_period).toLowerCase()}` : "charged once"}` }}
          status={{ label: feeInForce(fee, today) ? "In force" : fee.is_active ? "Not in force" : "Stopped", dot: feeInForce(fee, today) ? "bg-emerald-500" : "bg-gray-400" }}
          facts={[
            ["From", day(fee.effective_from)],
            ["Until", day(fee.effective_until) || "No end date"],
            ["Every boarder pays it", fee.is_mandatory ? "Yes" : "No"],
          ]}
          sections={[{ title: "Description", body: fee.description }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <button type="button" disabled={stop.isPending} onClick={() => stop.mutate(fee)} className="btn btn-outline">{fee.is_active ? "Stop using it" : "Use it again"}</button>
              <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(fee)}>
                <Pencil className="h-4 w-4" /> Edit
              </button>
            </>
          }
        >
          {stop.isError && errorLine(stop.error, "The fee could not be changed.")}
        </DetailsModal>
      )}

      {editing && (
        <FeeForm
          fee={editing === "new" ? undefined : editing}
          hostels={hostels.filter((row) => row.is_active)}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.name} added at ${money(saved.amount)}.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
